import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail, User, Building2, Briefcase, Calculator, FileSearch, Wrench, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import RgpdCheckbox from '../components/RgpdCheckbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: 'partenaire', label: 'Partenaire', subtitle: 'Marque Blanche', icon: Building2, accent: 'border-purple-500 bg-purple-500/10', dot: 'bg-purple-500' },
  { id: 'affilie',    label: 'Affilié',    subtitle: 'Apporteur d\'affaires', icon: Briefcase, accent: 'border-amber-500 bg-amber-500/10', dot: 'bg-amber-500' },
  { id: 'chiffreur',  label: 'Chiffreur',  subtitle: 'Devis & Chiffrage', icon: Calculator, accent: 'border-emerald-500 bg-emerald-500/10', dot: 'bg-emerald-500' },
  { id: 'analyste',   label: 'Analyste',   subtitle: 'Audit & Analyse', icon: FileSearch, accent: 'border-blue-500 bg-blue-500/10', dot: 'bg-blue-500' },
  { id: 'artisan',    label: 'Artisan / Société', subtitle: 'Travaux & Rénovation', icon: Wrench, accent: 'border-orange-500 bg-orange-500/10', dot: 'bg-orange-500' },
];

// Pays étrangers supportés
const PAYS_ETRANGERS = [
  { key: 'algerie', flag: '🇩🇿', label: 'Algérie',  currency: 'DZD', price: '15 000 DZD/mois', done: false },
  { key: 'maroc',   flag: '🇲🇦', label: 'Maroc',    currency: 'MAD', price: '1 000 MAD/mois',  done: false },
  { key: 'tunisie', flag: '🇹🇳', label: 'Tunisie',  currency: 'TND', price: '300 TND/mois',    done: false },
  { key: 'senegal', flag: '🇸🇳', label: 'Sénégal',  currency: 'XOF', price: '65 000 CFA/mois', done: false },
  { key: 'autre',   flag: '🌍',   label: 'Autre pays', currency: '—', price: 'Prix personnalisé', done: false },
];

export default function ReseauLeadCapturePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    nom: '',
    prenom: '',
    member_type: '',
    zone_pays: 'france',   // 'france' | 'etranger'
    country: 'france',     // code pays final
  });
  const [loading, setLoading] = useState(false);
  const [rgpdChecked, setRgpdChecked] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('L\'email est obligatoire');
      return;
    }
    if (!formData.member_type) {
      toast.error('Veuillez sélectionner votre rôle');
      return;
    }

    setLoading(true);
    try {
      // 1) Capture du lead
      await axios.post(`${API}/reseau/lead`, {
        email: formData.email,
        nom: formData.nom || null,
        prenom: formData.prenom || null,
        member_type: formData.member_type,
      });

      // 2) Stockage en sessionStorage pour pré-remplir le formulaire complet ensuite
      sessionStorage.setItem('reseau_lead_prefill', JSON.stringify({
        email: formData.email,
        nom: formData.nom,
        prenom: formData.prenom,
        member_type: formData.member_type,
        country: formData.country,
      }));

      // 3) Redirection vers le formulaire complet du rôle choisi
      const countryParam = formData.member_type === 'partenaire' && formData.country !== 'france'
        ? `?country=${formData.country}`
        : '';
      navigate(`/reseau/${formData.member_type}/inscription${countryParam}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex flex-col">
      <header className="py-4 sm:py-6 px-3 sm:px-6 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-4 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-white/70 hover:text-white hover:bg-white/10 px-2 sm:px-3 shrink-0"
              data-testid="lead-back-btn"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Retour</span>
            </Button>
            <h1
              className="font-heading text-base sm:text-xl font-bold text-white cursor-pointer whitespace-nowrap"
              onClick={() => navigate('/')}
            >
              L'ŒIL DU MARCHAND
            </h1>
          </div>
          <span className="text-emerald-400 font-medium text-[11px] sm:text-base text-right shrink-0 leading-tight max-w-[90px] sm:max-w-none">REJOINDRE LE RÉSEAU</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
              Démarrons ensemble
            </h2>
            <p className="text-white/70 text-base max-w-xl mx-auto">
              Renseignez votre email et choisissez votre rôle. Vous serez ensuite dirigé vers le formulaire d'inscription complet.
            </p>
          </div>

          <Card className="border-0 shadow-2xl bg-white/5 backdrop-blur">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email + Nom + Prénom */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <Label className="text-white/80">Email <span className="text-red-400">*</span></Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="votre@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        data-testid="lead-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/80">Nom <span className="text-white/40 text-xs">(facultatif)</span></Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Dupont"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        data-testid="lead-nom"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-white/80">Prénom <span className="text-white/40 text-xs">(facultatif)</span></Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Jean"
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        data-testid="lead-prenom"
                      />
                    </div>
                  </div>
                </div>

                {/* Choix du rôle (radios stylés en cards) */}
                <div>
                  <Label className="text-white/80 mb-3 block">
                    Je souhaite rejoindre comme <span className="text-red-400">*</span>
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {ROLES.map((role) => {
                      const Icon = role.icon;
                      const isSelected = formData.member_type === role.id;
                      return (
                        <button
                          key={role.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, member_type: role.id })}
                          className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                            isSelected
                              ? `${role.accent} scale-[1.02]`
                              : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'
                          }`}
                          data-testid={`lead-role-${role.id}`}
                        >
                          {isSelected && (
                            <span className={`absolute top-2 right-2 w-3 h-3 rounded-full ${role.dot}`} />
                          )}
                          <Icon className="w-6 h-6 text-white/80 mb-2" />
                          <p className="font-semibold text-white text-sm">{role.label}</p>
                          <p className="text-white/50 text-xs">{role.subtitle}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sélecteur pays — uniquement si Partenaire sélectionné */}
                {formData.member_type === 'partenaire' && (
                  <div className="border-t border-white/10 pt-5 space-y-4">
                    <Label className="text-white/80 block">
                      Pays d'activité <span className="text-red-400">*</span>
                    </Label>

                    {/* Niveau 1 : France ou Étranger */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'france',   flag: '🇫🇷', label: 'France',   sub: '100 €/mois',       accent: 'border-blue-500 bg-blue-500/10',    dot: 'bg-blue-500' },
                        { key: 'etranger', flag: '🌍',   label: 'Étranger', sub: 'Sélectionner pays', accent: 'border-indigo-500 bg-indigo-500/10', dot: 'bg-indigo-500' },
                      ].map(z => {
                        const isSelected = formData.zone_pays === z.key;
                        return (
                          <button key={z.key} type="button"
                            onClick={() => setFormData({
                              ...formData,
                              zone_pays: z.key,
                              country: z.key === 'france' ? 'france' : (formData.country === 'france' ? 'algerie' : formData.country),
                            })}
                            className={`relative text-left p-4 rounded-xl border-2 transition-all ${isSelected ? `${z.accent} scale-[1.02]` : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'}`}
                            data-testid={`lead-zone-${z.key}`}
                          >
                            {isSelected && <span className={`absolute top-2 right-2 w-3 h-3 rounded-full ${z.dot}`} />}
                            <span className="text-2xl block mb-1">{z.flag}</span>
                            <p className="font-semibold text-white text-sm">{z.label}</p>
                            <p className="text-white/50 text-xs">{z.sub}</p>
                          </button>
                        );
                      })}
                    </div>

                    {/* Niveau 2 : liste des pays étrangers */}
                    {formData.zone_pays === 'etranger' && (
                      <div>
                        <p className="text-white/60 text-sm mb-2">Choisissez votre pays :</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {PAYS_ETRANGERS.map(p => {
                            const isSelected = formData.country === p.key;
                            return (
                              <button key={p.key} type="button"
                                onClick={() => setFormData({ ...formData, country: p.key })}
                                className={`relative text-left p-3 rounded-xl border-2 transition-all ${
                                  isSelected ? 'border-emerald-500 bg-emerald-500/10 scale-[1.02]' : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'
                                } ${!p.done ? 'opacity-70' : ''}`}
                                data-testid={`lead-country-${p.key}`}
                              >
                                {isSelected && <span className="absolute top-2 right-2 w-3 h-3 rounded-full bg-emerald-500" />}
                                {!p.done && (
                                  <span className="absolute top-1.5 right-1.5 text-[9px] bg-amber-500/80 text-white px-1 py-0.5 rounded font-medium">Bientôt</span>
                                )}
                                <span className="text-xl block mb-1">{p.flag}</span>
                                <p className="font-semibold text-white text-xs">{p.label}</p>
                                <p className="text-white/40 text-[10px]">{p.currency} · {p.price}</p>
                              </button>
                            );
                          })}
                        </div>
                        {!PAYS_ETRANGERS.find(p => p.key === formData.country)?.done && formData.country !== 'france' && (
                          <p className="text-amber-400 text-xs mt-2">
                            Ce pays est en cours d'intégration. Vous pouvez vous inscrire, votre accès sera activé dès que disponible.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Submit */}
                <RgpdCheckbox checked={rgpdChecked} onChange={setRgpdChecked} companyName={null} />
                <Button
                  type="submit"
                  disabled={loading || !rgpdChecked}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-6 mt-3"
                  data-testid="lead-submit"
                >
                  {loading ? 'Envoi…' : "S'inscrire"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-white/50 text-sm">
                  Déjà inscrit ?{' '}
                  <button
                    onClick={() => navigate('/reseau/login')}
                    className="text-emerald-400 hover:underline"
                    data-testid="lead-already-registered"
                  >
                    Se connecter
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="py-4 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-white/40 text-sm">
            L'Œil du Marchand - Réseau de Professionnels
          </p>
        </div>
      </footer>
    </div>
  );
}
